<template>
  <div class="category-manage">
    <div class="page-header">
      <h2>栏目管理</h2>
      <p>管理学习栏目，每个栏目可包含多个视频内容</p>
    </div>

    <el-card class="filter-card">
      <div class="filter-actions">
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增栏目</el-button>
        <el-button type="info" icon="el-icon-refresh" @click="refresh">刷新</el-button>
      </div>
    </el-card>

    <el-card>
      <el-table :data="categoryList" border style="width: 100%" v-loading="loading">
        <el-table-column label="封面" width="120" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.cover"
              style="width: 100px; height: 60px"
              :src="getFullUrl(scope.row.cover)"
              fit="cover"
              :preview-src-list="[getFullUrl(scope.row.cover)]"
            ></el-image>
            <span v-else class="no-cover">未设置</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="栏目名称" min-width="180"></el-table-column>
        <el-table-column prop="description" label="描述" min-width="220">
          <template slot-scope="scope">
            {{ scope.row.description || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="视频数量" width="100" align="center">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ scope.row.videos_count || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="80" align="center"></el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.created_at }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" icon="el-icon-edit" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="danger" icon="el-icon-delete" size="mini" @click="handleDelete(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="540px" @close="resetForm">
      <el-form ref="categoryForm" :model="formData" :rules="formRules" label-width="80px">
        <el-form-item label="栏目名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入栏目名称" maxlength="50" show-word-limit />
        </el-form-item>
        
        <el-form-item label="栏目描述" prop="description">
          <el-input v-model="formData.description" type="textarea" :rows="3" placeholder="请输入栏目描述" maxlength="200" show-word-limit />
        </el-form-item>
        
        <el-form-item label="栏目封面" prop="cover">
          <el-upload
            class="cover-uploader"
            :action="uploadUrl"
            :headers="uploadHeaders"
            name="file"
            :show-file-list="false"
            accept="image/*"
            :on-success="handleCoverSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeCoverUpload"
          >
            <div class="upload-area" v-if="!formData.cover">
              <i class="el-icon-plus avatar-uploader-icon"></i>
              <span>点击上传封面图</span>
            </div>
            <div class="upload-preview" v-else>
              <el-image :src="getFullUrl(formData.cover)" style="width: 100px; height: 60px;" fit="cover"></el-image>
            <div class="cover-remove" @click.stop="removeCover">删除</div>
            </div>
          </el-upload>
          <div class="upload-tip">建议尺寸：750 x 400 像素，支持 jpg、png 格式，大小不超过 5MB</div>
        </el-form-item>
        
        <el-form-item label="排序">
          <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确认</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import config from '@/config'

export default {
  name: 'CategoryManagement',
  data() {
    return {
      loading: false,
      dialogVisible: false,
      submitting: false,
      isEdit: false,
      currentId: null,
      uploadUrl: config.apiBaseUrl + '/upload',
      uploadHeaders: {
        Authorization: `Bearer ${localStorage.getItem('admin-token')}`
      },
      staticUrl: config.staticUrl,
      formData: {
        name: '',
        description: '',
        cover: '',
        sort_order: 0
      },
      formRules: {
        name: [
          { required: true, message: '请输入栏目名称', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['categories']),
    categoryList() {
      return this.categories
    },
    dialogTitle() {
      return this.isEdit ? '编辑栏目' : '新增栏目'
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    getFullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return this.staticUrl + url
    },
    async loadData() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchCategories')
      } catch (error) {
        this.$message.error('加载栏目列表失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    async refresh() {
      await this.loadData()
      this.$message.success('刷新成功')
    },
    handleAdd() {
      this.isEdit = false
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.currentId = row.id
      this.formData = {
        name: row.name,
        description: row.description || '',
        cover: row.cover || '',
        sort_order: row.sort_order || 0
      }
      this.dialogVisible = true
    },
    async handleDelete(id) {
      try {
        await this.$confirm('删除栏目不会删除其中的视频，但视频将变为未分类状态。确定要删除吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await this.$store.dispatch('deleteCategory', id)
        this.$message.success('删除成功')
      } catch {
        // 用户取消了删除
      }
    },
    beforeCoverUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isImage) {
        this.$message.warning('只能上传图片文件！')
        return false
      }
      if (!isLt5M) {
        this.$message.warning('图片大小不能超过 5MB！')
        return false
      }
      return true
    },
    handleCoverSuccess(response) {
      if (response.code === 200 && response.data.url) {
        this.formData.cover = response.data.url
        this.$message.success('封面上传成功')
      } else {
        this.$message.error(response.message || '上传失败')
      }
    },
    handleUploadError() {
      this.$message.error('上传失败，请检查网络连接')
    },
    removeCover() {
      this.formData.cover = ''
    },
    submitForm() {
      this.$refs.categoryForm.validate(async valid => {
        if (valid) {
          this.submitting = true
          try {
            const submitData = {
              name: this.formData.name,
              description: this.formData.description,
              cover: this.formData.cover,
              sort_order: this.formData.sort_order
            }

            if (this.isEdit) {
              await this.$store.dispatch('updateCategory', {
                id: this.currentId,
                category: submitData
              })
              this.$message.success('更新成功')
            } else {
              await this.$store.dispatch('addCategory', submitData)
              this.$message.success('新增成功')
            }
            this.dialogVisible = false
            this.resetForm()
          } catch (error) {
            this.$message.error(error.message)
          } finally {
            this.submitting = false
          }
        }
      })
    },
    resetForm() {
      if (this.$refs.categoryForm) {
        this.$refs.categoryForm.resetFields()
      }
      this.isEdit = false
      this.currentId = null
      this.formData = {
        name: '',
        description: '',
        cover: '',
        sort_order: 0
      }
    }
  }
}
</script>

<style scoped>
.category-manage {
  padding: 20px;
}
.page-header {
  margin-bottom: 24px;
}
.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}
.page-header p {
  margin: 0;
  color: #999;
  font-size: 14px;
}
.filter-card {
  margin-bottom: 20px;
}
.filter-actions {
  display: flex;
  gap: 10rpx;
}

/* 封面上传 */
.cover-uploader {
  display: block;
  margin-top: 8rpx;
}
.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 140rpx;
  border: 1px dashed #d9d9d9;
  border-radius: 6rpx;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;
}
.upload-area:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}
.avatar-uploader-icon {
  font-size: 32px;
  color: #c0c4cc;
  margin-bottom: 10rpx;
}
.upload-area span {
  font-size: 14rpx;
  color: #909399;
}
.upload-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80rpx;
  border: 1px solid #e4e7ed;
  border-radius: 6rpx;
  padding: 0;
}
.cover-remove {
  position: absolute;
  right: -12px;
  top: -12rpx;
  width: 20rpx;
  height: 20rpx;
  border-radius: 50%;
  background: #f56c6c;
  color: #fff;
  font-size: 14px;
  line-height: 20rpx;
  text-align: center;
  z-index: 1;
}
.upload-tip {
  font-size: 12px;
  color: #bbb;
  margin-top: 6rpx;
}
.no-cover {
  display: inline-block;
  padding: 4rpx 12rpx;
  font-size: 13px;
  color: #ccc;
  background: #f5f7fa;
  border-radius: 4rpx;
}
</style>
